import { useState, useRef, useEffect } from 'react'
import { Bell, AlertCircle, RefreshCw } from 'lucide-react'
import { useGetNotificationStatisticsQuery, useGetNotificationsQuery } from '@/store'
import { NotificationList } from './NotificationList'

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    data: stats,
    error: statsError,
    isLoading: isStatsLoading,
    isError: isStatsError,
    refetch: refetchStats
  } = useGetNotificationStatisticsQuery(undefined, {
    pollingInterval: 10000, // Poll every 10 seconds (faster updates)
    refetchOnMountOrArgChange: true, // Refetch when component mounts
    refetchOnFocus: true, // Refetch when window regains focus
  })

  const {
    data: notificationsData,
    isLoading: isNotificationsLoading
  } = useGetNotificationsQuery(
    {
      unreadOnly: false,
      limit: 10,
      page: 1,
    },
    {
      skip: !isOpen, // Only fetch when dropdown is open
    }
  )

  const unreadCount = stats?.unreadCount || 0
  const notifications = notificationsData?.data || []

  // Debug logging
  useEffect(() => {
    if (isStatsError) {
      console.error('🔔 Notification Stats Error:', statsError)
      console.log('📍 Current token:', localStorage.getItem('auth_token'))
      console.log('📍 Is logged in:', localStorage.getItem('isLoggedIn'))
    }
  }, [isStatsError, statsError])

  useEffect(() => {
    if (stats) {
      console.log('🔔 Notification Stats:', stats)
      console.log('📊 Unread count:', unreadCount)
    }
  }, [stats, unreadCount])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-lg transition-colors"
        aria-label="Notifications"
        title={isStatsError ? 'Error loading notifications' : `${unreadCount} unread notifications`}
      >
        <Bell className="w-6 h-6" />

        {/* Error indicator (skip 401 as it will auto-redirect) */}
        {isStatsError && (statsError as any)?.status !== 401 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-3 h-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-error opacity-75 animate-ping"></span>
            <AlertCircle className="w-3 h-3 text-error" />
          </span>
        )}

        {/* Unread count badge */}
        {!isStatsError && unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-error rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {/* Loading indicator */}
        {isStatsLoading && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4">
            <span className="inline-block w-3 h-3 border-2 border-primary-800 border-t-transparent rounded-full animate-spin"></span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-border-light z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-light">
            <h3 className="text-lg font-semibold text-text-primary">การแจ้งเตือน</h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <span className="text-sm text-text-tertiary">
                  {unreadCount} ใหม่
                </span>
              )}
              <button
                onClick={() => refetchStats()}
                className="p-1.5 text-text-tertiary hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                title="รีเฟรชการแจ้งเตือน"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {/* Error State */}
            {isStatsError && (statsError as any)?.status !== 401 && (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <AlertCircle className="w-12 h-12 mb-3 text-error opacity-50" />
                <p className="text-sm font-medium text-text-primary mb-1">
                  เกิดข้อผิดพลาดในการโหลดการแจ้งเตือน
                </p>
                <p className="text-xs text-text-tertiary mb-4">
                  {(statsError as any)?.data?.message || 'กรุณาลองใหม่อีกครั้ง'}
                </p>
                <button
                  onClick={() => refetchStats()}
                  className="px-4 py-2 text-sm bg-primary-800 text-white rounded-lg hover:bg-primary-900"
                >
                  ลองอีกครั้ง
                </button>
              </div>
            )}

            {/* Loading State */}
            {isNotificationsLoading && !isStatsError && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-primary-800 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-sm text-text-tertiary">กำลังโหลด...</p>
              </div>
            )}

            {/* Empty State */}
            {!isStatsError && !isNotificationsLoading && notifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-text-tertiary">
                <Bell className="w-12 h-12 mb-3 opacity-30" />
                <p>ไม่มีการแจ้งเตือน</p>
              </div>
            )}

            {/* Notification List */}
            {!isStatsError && !isNotificationsLoading && notifications.length > 0 && (
              <NotificationList
                notifications={notifications}
                onClose={() => setIsOpen(false)}
              />
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-border-light">
              <button
                onClick={() => {
                  setIsOpen(false)
                  // Navigate to full notifications page
                  window.location.href = '/notifications'
                }}
                className="w-full text-center text-sm text-primary-800 hover:text-primary-900 font-medium"
              >
                ดูทั้งหมด
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
